import React, { useCallback, useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, AlertCircle, Undo2 } from "lucide-react";
import { TASK_STATUSES } from "@/lib/roles";
import { format, isPast, isToday } from "date-fns";
import { toast } from "sonner";

const PRIORITY_COLOR = {
  low: { bg: "#F1F5F9", fg: "#475569" },
  medium: { bg: "#DBEAFE", fg: "#1D4ED8" },
  high: { bg: "#FEE2E2", fg: "#B91C1C" },
};

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [scope, setScope] = useState("active");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", assignee_id: "self", due_at: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const params = filter === "mine" ? { mine: true } : filter === "open" ? { status: "open" } : {};
    if (scope === "archived") params.only_archived = true;
    setTasks((await api.get("/tasks", { params })).data);
  }, [filter, scope]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/users").then(r => setUsers(r.data)); }, []);

  const toggle = async (t) => {
    const next = t.status === "done" ? "open" : "done";
    await api.patch(`/tasks/${t.id}`, { status: next });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Archive this task?")) return;
    await api.delete(`/tasks/${id}`);
    toast.success("Archived");
    load();
  };
  const restore = async (id) => {
    await api.post(`/tasks/${id}/restore`);
    toast.success("Restored");
    load();
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        title: form.title, description: form.description, priority: form.priority,
        due_at: form.due_at ? new Date(form.due_at).toISOString() : null,
      };
      if (form.assignee_id && form.assignee_id !== "self") payload.assignee_id = form.assignee_id;
      await api.post("/tasks", payload);
      toast.success("Task created");
      setOpen(false);
      setForm({ title: "", description: "", priority: "medium", assignee_id: "self", due_at: "" });
      load();
    } catch (e) { toast.error("Create failed"); }
    finally { setSaving(false); }
  };

  const userName = (id) => users.find(u => u.id === id)?.name || "—";

  return (
    <AppLayout title="Tasks">
      <div className="flex items-center gap-3 mb-5">
        <div className="flex bg-white border border-[var(--miro-border)] rounded-lg p-0.5">
          {[["all","All"],["mine","Mine"],["open","Open"]].map(([v,l]) => (
            <button key={v} data-testid={`tasks-filter-${v}`}
                    onClick={() => setFilter(v)}
                    className={`h-8 px-3 rounded-md text-xs font-semibold transition-colors ${filter === v ? "bg-[var(--miro-navy)] text-white" : "text-[var(--miro-text-secondary)]"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex bg-white border border-[var(--miro-border)] rounded-lg p-0.5">
          {[["active","Active"],["archived","Archived"]].map(([v,l]) => (
            <button key={v} data-testid={`tasks-scope-${v}`}
                    onClick={() => setScope(v)}
                    className={`h-8 px-3 rounded-md text-xs font-semibold transition-colors ${scope === v ? "bg-[var(--miro-navy)] text-white" : "text-[var(--miro-text-secondary)]"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="ml-auto">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-task-btn" style={{ background: "var(--miro-navy)" }}><Plus size={16} className="mr-1"/> New task</Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle className="font-display text-2xl text-[var(--miro-navy)]">New task</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title *</Label><Input data-testid="new-task-title" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} className="mt-1"/></div>
                <div><Label>Description</Label><Textarea data-testid="new-task-description" rows={3} value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} className="mt-1"/></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Priority</Label>
                    <Select value={form.priority} onValueChange={(v)=>setForm({...form,priority:v})}>
                      <SelectTrigger data-testid="new-task-priority" className="mt-1"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Due</Label>
                    <Input data-testid="new-task-due" type="datetime-local" value={form.due_at} onChange={(e)=>setForm({...form,due_at:e.target.value})} className="mt-1"/>
                  </div>
                </div>
                <div>
                  <Label>Assignee</Label>
                  <Select value={form.assignee_id} onValueChange={(v)=>setForm({...form,assignee_id:v})}>
                    <SelectTrigger data-testid="new-task-assignee" className="mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="self">Assign to me</SelectItem>
                      {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="new-task-submit" disabled={!form.title.trim() || saving} onClick={submit} style={{ background: "var(--miro-navy)" }}>
                  {saving ? "Creating…" : "Create task"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="space-y-2">
        {tasks.length === 0 && (
          <div className="text-center py-16 text-[var(--miro-text-muted)]">No tasks in this view.</div>
        )}
        {tasks.map(t => {
          const overdue = t.due_at && isPast(new Date(t.due_at)) && t.status !== "done";
          const dueToday = t.due_at && isToday(new Date(t.due_at));
          return (
            <div key={t.id} data-testid={`task-row-${t.id}`}
                 className={`miro-card p-4 flex items-center gap-4 ${t.status === "done" ? "opacity-60" : ""}`}>
              <Checkbox
                data-testid={`task-check-${t.id}`}
                checked={t.status === "done"}
                onCheckedChange={() => toggle(t)}
              />
              <div className="flex-1 min-w-0">
                <div className={`font-semibold text-[var(--miro-navy)] ${t.status === "done" ? "line-through" : ""}`}>
                  {t.title}
                </div>
                {t.description && <div className="text-xs text-[var(--miro-text-secondary)] mt-0.5 truncate">{t.description}</div>}
                <div className="flex items-center gap-3 mt-2 text-[11px] text-[var(--miro-text-muted)]">
                  {t.due_at && (
                    <span className={`flex items-center gap-1 ${overdue ? "text-red-600 font-semibold" : dueToday ? "text-[var(--miro-blue-600)] font-semibold" : ""}`}>
                      {overdue && <AlertCircle size={12}/>}
                      {format(new Date(t.due_at), "MMM d, HH:mm")}
                    </span>
                  )}
                  {t.assignee_id && <span>· {userName(t.assignee_id)}</span>}
                </div>
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded"
                    style={{ background: PRIORITY_COLOR[t.priority].bg, color: PRIORITY_COLOR[t.priority].fg }}>
                {t.priority}
              </span>
              {scope === "archived" ? (
                <button data-testid={`task-restore-${t.id}`} onClick={() => restore(t.id)}
                        className="text-[var(--miro-text-muted)] hover:text-green-600 transition-colors" title="Restore">
                  <Undo2 size={15}/>
                </button>
              ) : (
                <button data-testid={`task-delete-${t.id}`} onClick={() => remove(t.id)}
                        className="text-[var(--miro-text-muted)] hover:text-red-600 transition-colors" title="Archive">
                  <Trash2 size={15}/>
                </button>
              )}
            </div>
          );
        })}
      </div>
    </AppLayout>
  );
}
